const ACTIVE_PAYMENT_STATUSES = new Set(['approved', 'paid', 'settled']);

const toAmount = (value) => {
  const parsed = parseFloat(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundMoney = (value) => Math.round((toAmount(value) + Number.EPSILON) * 100) / 100;

const roundNearest = (value, increment = 10) => {
  const amount = toAmount(value);
  if (!amount) return 0;
  const sign = amount < 0 ? -1 : 1;
  return sign * Math.floor(Math.abs(amount) / increment + 0.5) * increment;
};

// Salary values use the same nearest-Rs.10 model as the backend payroll ledger.
export const roundSalaryDisplayAmount = (value) => {
  return roundNearest(value, 10);
};
const normalizeBalance = (value) => {
  const rounded = roundMoney(value);
  return Math.abs(rounded) <= 0.01 ? 0 : rounded;
};

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(`${dateStr}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const START_EVENT_TYPES = new Set(['employed', 'reemployed']);

const normalizeDateString = (value) => String(value || '').substring(0, 10);
const minDateString = (a, b) => (a && b ? (a < b ? a : b) : (a || b || ''));
const maxDateString = (a, b) => (a && b ? (a > b ? a : b) : (a || b || ''));

const sortLifecycleEvents = (events = []) => (
  [...events].sort((a, b) => (
    normalizeDateString(a.event_date).localeCompare(normalizeDateString(b.event_date))
    || String(a.created_at || '').localeCompare(String(b.created_at || ''))
    || String(a.id || '').localeCompare(String(b.id || ''))
  ))
);

const addDays = (dateStr, days) => {
  const date = parseDate(dateStr);
  if (!date) return '';
  date.setDate(date.getDate() + days);
  return formatDate(date);
};

const diffDaysInclusive = (startStr, endStr) => {
  const start = parseDate(startStr);
  const end = parseDate(endStr);
  if (!start || !end) return 0;
  return Math.max(0, Math.round((end - start) / 86400000) + 1);
};

const isActivePayment = (payment) => ACTIVE_PAYMENT_STATUSES.has(payment.status || 'approved');
const getStaffPaymentId = (payment) => String(payment.staff ?? payment.staff_id ?? '');

const getStaffPayments = (payments, staffId) => (
  (payments || [])
    .filter(isActivePayment)
    .filter(payment => getStaffPaymentId(payment) === String(staffId))
    .filter(payment => payment.advance_date)
    .sort((a, b) => String(a.advance_date).localeCompare(String(b.advance_date)) || String(a.id || '').localeCompare(String(b.id || '')))
);

export const doesStaffHavePayrollActivityInRange = (member, payments, fromDateStr, toDateStr) => {
  if (!member || !fromDateStr || !toDateStr) return false;
  return getStaffPayments(payments, member.id).some((payment) => (
    payment.advance_date >= fromDateStr && payment.advance_date <= toDateStr
  ));
};

export const getSalaryDueDateString = (member, year, monthIndex) => {
  const salaryDay = parseInt(member?.salary_date || 1, 10);
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  return formatDate(new Date(year, monthIndex, Math.min(salaryDay, daysInMonth)));
};

const getPreviousSalaryDueDateString = (member, dueDateStr) => {
  const dueDate = parseDate(dueDateStr);
  if (!dueDate) return '';
  return getSalaryDueDateString(member, dueDate.getFullYear(), dueDate.getMonth() - 1);
};

const getNextSalaryDueDateString = (member, dueDateStr) => {
  const dueDate = parseDate(dueDateStr);
  if (!dueDate) return '';
  return getSalaryDueDateString(member, dueDate.getFullYear(), dueDate.getMonth() + 1);
};

const getFirstDueOnOrAfter = (member, startDateStr) => {
  const start = parseDate(startDateStr);
  if (!start) return '';
  let due = getSalaryDueDateString(member, start.getFullYear(), start.getMonth());
  if (due < startDateStr) {
    due = getNextSalaryDueDateString(member, due);
  }
  return due;
};

const getFirstDueAfter = (member, dateStr) => {
  let due = getFirstDueOnOrAfter(member, dateStr);
  if (due && due <= dateStr) {
    due = getNextSalaryDueDateString(member, due);
  }
  return due;
};

const buildFallbackServicePeriods = (member) => {
  if (!member?.hire_date) return [];
  return [{
    start_date: member.hire_date,
    end_date: member.termination_date || '',
  }];
};

const buildServicePeriodsFromEvents = (member, lifecycleEvents = []) => {
  const events = sortLifecycleEvents(lifecycleEvents);
  const rawHireDate = normalizeDateString(member?.hire_date);
  const rawTerminationDate = normalizeDateString(member?.termination_date);
  if (!events.length) return buildFallbackServicePeriods(member);

  const periods = [];
  let currentStart = rawHireDate || '';

  events.forEach((event) => {
    const eventDate = normalizeDateString(event.event_date);
    if (!eventDate) return;

    if (START_EVENT_TYPES.has(event.event_type)) {
      if (!currentStart) {
        currentStart = eventDate;
      } else if (eventDate < currentStart) {
        currentStart = eventDate;
      }
      return;
    }

    if (event.event_type === 'terminated') {
      if (!currentStart) {
        const fallbackStart = rawHireDate;
        if (fallbackStart && fallbackStart < eventDate) {
          currentStart = fallbackStart;
        }
      }
      if (currentStart && currentStart < eventDate) {
        periods.push({ start_date: currentStart, end_date: eventDate });
      }
      currentStart = '';
    }
  });

  if (currentStart) {
    if (rawTerminationDate && currentStart < rawTerminationDate) {
      periods.push({ start_date: currentStart, end_date: rawTerminationDate });
    } else if (!rawTerminationDate) {
      periods.push({ start_date: currentStart, end_date: '' });
    }
  }

  if (!periods.length) return buildFallbackServicePeriods(member);
  return periods;
};

const getServicePeriods = (member) => {
  if (!member) return [];
  const rawHireDate = normalizeDateString(member?.hire_date);
  const rawTerminationDate = normalizeDateString(member?.termination_date);
  if (Array.isArray(member.service_periods) && member.service_periods.length > 0) {
    const normalized = member.service_periods
      .map((period) => ({
        start_date: normalizeDateString(period.start_date),
        end_date: normalizeDateString(period.end_date),
      }))
      .filter((period) => period.start_date)
      .map((period) => {
        if (rawTerminationDate && (!period.end_date || period.end_date > rawTerminationDate) && period.start_date < rawTerminationDate) {
          return { ...period, end_date: rawTerminationDate };
        }
        return period;
      })
      .filter((period) => !rawTerminationDate || period.start_date < rawTerminationDate)
      .sort((a, b) => a.start_date.localeCompare(b.start_date) || a.end_date.localeCompare(b.end_date));
    if (normalized.length > 0) return normalized;
    if (rawHireDate) return [{ start_date: rawHireDate, end_date: rawTerminationDate || '' }];
    return [];
  }
  const lifecycleEvents = Array.isArray(member.lifecycle_events) ? member.lifecycle_events : [];
  return buildServicePeriodsFromEvents(member, lifecycleEvents);
};

export const mergeStaffWithLifecycleHistory = (staffList = [], lifecycleEvents = []) => {
  const eventsByStaff = {};
  (lifecycleEvents || []).forEach((event) => {
    const staffId = String(event.staff ?? event.staff_id ?? '');
    if (!staffId) return;
    if (!eventsByStaff[staffId]) eventsByStaff[staffId] = [];
    eventsByStaff[staffId].push(event);
  });

  return (staffList || []).map((member) => {
    const servicePeriods = buildServicePeriodsFromEvents(member, eventsByStaff[String(member.id)] || member.lifecycle_events || []);
    return {
      ...member,
      lifecycle_events: sortLifecycleEvents(eventsByStaff[String(member.id)] || member.lifecycle_events || []),
      service_periods: servicePeriods,
    };
  });
};

// Effective wage type (salaried / daily_wages) for a staff member on a given date. Wage type is
// time-based, so this walks the wage_type_change lifecycle events rather than reading the current
// field (which only reflects today).
export const getStaffWageTypeOnDate = (member, dateStr) => {
  if (!member) return 'salaried';
  const ds = normalizeDateString(dateStr);
  const changes = (member.lifecycle_events || [])
    .filter((e) => e.event_type === 'wage_type_change' && e.event_date && e.wage_type)
    .sort((a, b) => String(a.event_date).localeCompare(String(b.event_date)));
  if (!changes.length || !ds) return member.wage_type || 'salaried';
  let wt = null;
  for (const c of changes) {
    if (String(c.event_date).substring(0, 10) <= ds) wt = c.wage_type;
    else break;
  }
  if (wt) return wt;
  return changes[0].previous_wage_type || member.wage_type || 'salaried';
};

const getTerminationDate = (member) => {
  if (!member) return '';
  const rawTerminationDate = normalizeDateString(member?.termination_date);
  if (rawTerminationDate) return rawTerminationDate;
  const servicePeriods = getServicePeriods(member);
  const hasOpenPeriod = servicePeriods.some((period) => !period.end_date);
  if (hasOpenPeriod) return '';
  if (member.employment_status === 'terminated') {
    return servicePeriods[servicePeriods.length - 1]?.end_date || member.termination_date || '';
  }
  return '';
};

// Exported termination date (empty string if currently employed / never terminated).
export const getStaffTerminationDate = (member) => getTerminationDate(member);

// Attributed display name for a staff dropdown option, evaluated for a date/range context —
// everything is time-bound: "· Daily"/"· Salaried" for the wage type effective then (latest in a
// range if it changed), and "(Terminated)" when their termination falls on/before the reference.
export const getStaffAttributedName = (member, { onDate = null, fromDate = null, toDate = null } = {}) => {
  const refDate = normalizeDateString(onDate || toDate || fromDate) || formatDate(new Date());
  const name = member?.full_name || `${member?.first_name || ''} ${member?.last_name || ''}`.trim();
  const wt = getStaffWageTypeOnDate(member, refDate);
  // Terminated status is time-bound: use the latest employed/re-employed/terminated event on/before
  // the reference date (so a since-rehired staff still reads "(Terminated)" for a past range they
  // were terminated in). Falls back to the raw termination_date when no events are present.
  const evs = (member?.lifecycle_events || [])
    .filter((e) => ['employed', 'reemployed', 'terminated'].includes(e.event_type) && e.event_date
      && String(e.event_date).substring(0, 10) <= String(refDate))
    .sort((a, b) => String(a.event_date).localeCompare(String(b.event_date)));
  let isTerminatedByRef;
  if (evs.length) {
    isTerminatedByRef = evs[evs.length - 1].event_type === 'terminated';
  } else {
    const term = getTerminationDate(member);
    isTerminatedByRef = term && String(term) <= String(refDate);
  }
  return `${name}${wt === 'daily_wages' ? ' · Daily' : ' · Salaried'}${isTerminatedByRef ? ' (Terminated)' : ''}`;
};

/**
 * THE centralized staff-dropdown builder. Returns [{ value, label }] filtered to staff who were in
 * service during the scope, with the time-bound attributed label. Pass `onDate` for a single-date
 * context (attendance/leave/off-day/performance forms) or `fromDate`+`toDate` for a range filter
 * (salary/attendance list filters). `keepValue` always keeps a given id (e.g. the currently-selected
 * value in an edit form) even if now inactive.
 */
export const buildStaffDropdownOptions = (staffList = [], { onDate = null, fromDate = null, toDate = null, keepValue = null } = {}) => {
  const today = formatDate(new Date());
  return (staffList || [])
    .filter((m) => {
      if (keepValue != null && String(m.id) === String(keepValue)) return true;
      if (onDate) return isStaffActiveOnDate(m, onDate);
      if (fromDate && toDate) return doesStaffOverlapDateRange(m, fromDate, toDate);
      return isStaffActiveOnDate(m, today);
    })
    .slice()
    .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || ''))
    .map((m) => ({ value: m.id, label: getStaffAttributedName(m, { onDate, fromDate, toDate }) }));
};

export const isStaffActiveOnDate = (member, dateStr) => {
  if (!member || !dateStr) return false;
  const serviceMatch = getServicePeriods(member).some((period) => (
    period.start_date <= dateStr
    && (!period.end_date || dateStr < period.end_date)
  ));
  if (serviceMatch) return true;
  const rawHireDate = normalizeDateString(member?.hire_date);
  const rawTerminationDate = normalizeDateString(member?.termination_date);
  return Boolean(rawHireDate && rawHireDate <= dateStr && (!rawTerminationDate || dateStr < rawTerminationDate));
};

export const doesStaffOverlapDateRange = (member, fromDateStr, toDateStr) => {
  if (!member || !fromDateStr || !toDateStr) return false;
  const serviceMatch = getServicePeriods(member).some((period) => (
    period.start_date <= toDateStr
    && (!period.end_date || period.end_date > fromDateStr)
  ));
  if (serviceMatch) return true;
  const rawHireDate = normalizeDateString(member?.hire_date);
  const rawTerminationDate = normalizeDateString(member?.termination_date);
  return Boolean(rawHireDate && rawHireDate <= toDateStr && (!rawTerminationDate || rawTerminationDate > fromDateStr));
};

const getCycleServiceSegments = (member, dueDateStr) => {
  if (!member || !dueDateStr) return [];
  const previousDue = getPreviousSalaryDueDateString(member, dueDateStr);
  const cycleStart = addDays(previousDue, 1);
  const cycleEnd = dueDateStr; // payday is the last day of the cycle (inclusive)
  if (!cycleStart || !cycleEnd || cycleEnd < cycleStart) return [];

  return getServicePeriods(member)
    .map((period) => {
      const overlapStart = maxDateString(period.start_date, cycleStart);
      const overlapEnd = minDateString(period.end_date ? addDays(period.end_date, -1) : cycleEnd, cycleEnd);
      if (!overlapStart || !overlapEnd || overlapEnd < overlapStart) return null;
      return {
        startDate: overlapStart,
        endDate: overlapEnd,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.startDate.localeCompare(b.startDate) || a.endDate.localeCompare(b.endDate));
};

const getTargetCycleSegment = (member, dueDateStr) => {
  const segments = getCycleServiceSegments(member, dueDateStr);
  return segments[segments.length - 1] || null;
};

export const getCycleSalaryAmount = (member, dueDateStr) => {
  const monthlySalary = toAmount(member?.monthly_salary);
  if (monthlySalary <= 0) return 0;
  const previousDue = getPreviousSalaryDueDateString(member, dueDateStr);
  const cycleStart = addDays(previousDue, 1);
  const cycleEnd = dueDateStr; // payday is the last day of the cycle (inclusive)
  if (!cycleStart || !cycleEnd || cycleEnd < cycleStart) return 0;

  const segments = getCycleServiceSegments(member, dueDateStr);
  if (!segments.length) return 0;

  // Prorate only when joining mid-cycle (first cycle) or leaving mid-cycle (last cycle).
  // Complete cycles always earn the full monthly salary regardless of month length.
  return roundSalaryDisplayAmount(
    segments.reduce((total, seg) => {
      const joiningPartial = seg.startDate > cycleStart;
      const leavingPartial = seg.endDate < cycleEnd;
      const segSalary = toAmount(member?.monthly_salary);
      if (!joiningPartial && !leavingPartial) return total + segSalary;
      const workedDays = diffDaysInclusive(seg.startDate, seg.endDate);
      if (workedDays <= 0) return total;
      return total + (segSalary / 30) * workedDays;
    }, 0)
  );
};

const getAdvanceImpact = (payment) => {
  if (payment.payment_type !== 'advance') return 0;
  return Math.max(0, toAmount(payment.amount) - toAmount(payment.repaid_amount));
};

const getSalaryAdjustmentImpact = (payment) => {
  if (payment.payment_type === 'advance') return getAdvanceImpact(payment);
  if (payment.payment_type === 'deduction') return toAmount(payment.amount);
  return 0;
};

const getFirstLedgerDate = (member, payments, fallbackDate) => {
  const paymentDates = payments.map(payment => payment.advance_date).filter(Boolean).sort();
  const firstServiceDate = getServicePeriods(member)[0]?.start_date || member?.hire_date || fallbackDate;
  if (paymentDates.length > 0) {
    const earliestPayment = paymentDates[0];
    if (firstServiceDate && firstServiceDate > earliestPayment) {
      return firstServiceDate;
    }
    return earliestPayment;
  }
  const dates = [firstServiceDate, fallbackDate].filter(Boolean).sort();
  return dates[0] || fallbackDate;
};

const getLedgerTargetDueDate = (member, asOfDateStr) => {
  if (!member || !asOfDateStr) return '';
  const asOf = parseDate(asOfDateStr);
  if (!asOf) return '';
  const currentDue = getSalaryDueDateString(member, asOf.getFullYear(), asOf.getMonth());
  return currentDue > asOfDateStr ? currentDue : getNextSalaryDueDateString(member, currentDue);
};

const getCycleStartDate = (member, dueDateStr) => addDays(getPreviousSalaryDueDateString(member, dueDateStr), 1);

const sumCycleSegmentDays = (member, dueDateStr, rangeEndStr) => {
  if (!member || !dueDateStr || !rangeEndStr) return 0;
  const cycleStart = getCycleStartDate(member, dueDateStr);
  const cycleEnd = dueDateStr; // payday is the last day of the cycle (inclusive)
  const boundedEnd = minDateString(rangeEndStr, cycleEnd);
  if (!cycleStart || !boundedEnd || boundedEnd < cycleStart) return 0;

  return getCycleServiceSegments(member, dueDateStr).reduce((sum, segment) => {
    const overlapStart = maxDateString(segment.startDate, cycleStart);
    const overlapEnd = minDateString(segment.endDate, boundedEnd);
    if (!overlapStart || !overlapEnd || overlapEnd < overlapStart) return sum;
    return sum + diffDaysInclusive(overlapStart, overlapEnd);
  }, 0);
};

const buildSalaryLedger = (member, payments, targetDueStr, asOfDateStr = targetDueStr) => {
  if (!member || !targetDueStr) {
    return { events: [], targetEvent: null, targetEvents: [], paymentCredit: 0, netBalance: 0 };
  }

  const eventEnd = asOfDateStr || targetDueStr;
  const staffPayments = getStaffPayments(payments, member.id).filter(payment => payment.advance_date <= eventEnd);
  const firstRelevantDate = getFirstLedgerDate(member, staffPayments, targetDueStr);
  let dueCursor = getFirstDueOnOrAfter(member, firstRelevantDate);
  if (!dueCursor || dueCursor > targetDueStr) {
    dueCursor = targetDueStr;
  }

  const events = [];
  const eventByDueDate = {};
  let guard = 0;
  while (dueCursor && dueCursor <= targetDueStr && guard < 240) {
    const segments = getCycleServiceSegments(member, dueCursor);
    segments.forEach((segment, segmentIndex) => {
      const event = {
        dueDate: dueCursor,
        cycleStart: getCycleStartDate(member, dueCursor),
        segmentStart: segment.startDate,
        segmentEnd: segment.endDate,
        segmentIndex,
        grossSalary: getCycleSalaryAmount({
          ...member,
          service_periods: [{ start_date: segment.startDate, end_date: addDays(segment.endDate, 1) }],
        }, dueCursor),
        adjustmentTotal: 0,
        advanceOnlyTotal: 0,
        charge: 0,
        allocatedSalary: 0,
        remaining: 0,
        cycleSalaryPaid: 0,
        pastCycleCarryApplied: 0,
        carryForwardApplied: 0,
      };
      events.push(event);
      if (!eventByDueDate[dueCursor]) eventByDueDate[dueCursor] = [];
      eventByDueDate[dueCursor].push(event);
    });
    dueCursor = getNextSalaryDueDateString(member, dueCursor);
    guard += 1;
  }

  events.sort((a, b) => (
    a.dueDate.localeCompare(b.dueDate)
    || String(a.segmentStart || '').localeCompare(String(b.segmentStart || ''))
    || (a.segmentIndex || 0) - (b.segmentIndex || 0)
  ));

  staffPayments
    .filter(payment => payment.payment_type !== 'salary')
    .forEach((payment) => {
      const dueDate = getFirstDueAfter(member, payment.advance_date);
      const dueEvents = (eventByDueDate[dueDate] || [])
        .filter(event => event.segmentStart <= payment.advance_date)
        .sort((a, b) => b.segmentStart.localeCompare(a.segmentStart) || b.segmentIndex - a.segmentIndex);
      const event = dueEvents[0];
      if (!event) return;
      event.adjustmentTotal = roundMoney(event.adjustmentTotal + getSalaryAdjustmentImpact(payment));
      event.advanceOnlyTotal = roundMoney(event.advanceOnlyTotal + getAdvanceImpact(payment));
    });

  let creditCarry = 0;
  let creditCarrySourceDueDate = '';
  events.forEach((event) => {
    const baseCharge = roundMoney(event.grossSalary - event.adjustmentTotal);
    const incomingCredit = roundMoney(creditCarry);
    const creditApplied = Math.min(Math.max(baseCharge, 0), incomingCredit);
    if (creditApplied > 0 && creditCarrySourceDueDate && creditCarrySourceDueDate < event.dueDate) {
      event.carryForwardApplied = roundMoney(creditApplied);
    }
    const rawCharge = roundMoney(baseCharge - incomingCredit);
    if (rawCharge < 0) {
      event.charge = 0;
      event.remaining = 0;
      creditCarry = roundMoney(-rawCharge);
      if (!(incomingCredit > Math.max(baseCharge, 0) && creditCarrySourceDueDate)) {
        creditCarrySourceDueDate = event.dueDate;
      }
    } else {
      event.charge = rawCharge;
      event.remaining = rawCharge;
      creditCarry = 0;
      creditCarrySourceDueDate = '';
    }
  });

  let paymentCredit = 0;
  staffPayments
    .filter(payment => payment.payment_type === 'salary')
    .forEach((payment) => {
      let amountLeft = toAmount(payment.amount);
      if (amountLeft <= 0) return;

      const candidates = events.filter(event => event.remaining > 0 && event.segmentStart <= payment.advance_date);

      candidates.forEach((event) => {
        if (amountLeft <= 0 || event.remaining <= 0) return;
        const applied = Math.min(event.remaining, amountLeft);
        event.remaining = roundMoney(event.remaining - applied);
        event.allocatedSalary = roundMoney(event.allocatedSalary + applied);
        if (event.dueDate === targetDueStr) {
          event.cycleSalaryPaid = roundMoney(event.cycleSalaryPaid + applied);
          if (payment.advance_date < event.cycleStart) {
            event.pastCycleCarryApplied = roundMoney(event.pastCycleCarryApplied + applied);
          }
        }
        amountLeft = roundMoney(amountLeft - applied);
      });

      if (amountLeft > 0) {
        const futureDue = getFirstDueAfter(member, payment.advance_date);
        const futureCandidates = events.filter(event => event.remaining > 0 && futureDue && event.dueDate >= futureDue);
        futureCandidates.forEach((event) => {
          if (amountLeft <= 0 || event.remaining <= 0) return;
          const applied = Math.min(event.remaining, amountLeft);
          event.remaining = roundMoney(event.remaining - applied);
          event.allocatedSalary = roundMoney(event.allocatedSalary + applied);
          if (event.dueDate === targetDueStr) {
            event.cycleSalaryPaid = roundMoney(event.cycleSalaryPaid + applied);
            if (payment.advance_date < event.cycleStart) {
              event.pastCycleCarryApplied = roundMoney(event.pastCycleCarryApplied + applied);
            }
          }
          amountLeft = roundMoney(amountLeft - applied);
        });
      }

      if (amountLeft > 0) {
        paymentCredit = roundMoney(paymentCredit + amountLeft);
      }
    });

  events.forEach((event) => {
    event.remaining = normalizeBalance(event.remaining);
  });

  const targetEvents = eventByDueDate[targetDueStr] || [];
  const targetEvent = targetEvents[targetEvents.length - 1] || null;
  const totalRemaining = roundMoney(events.reduce((sum, event) => sum + event.remaining, 0));
  const netBalance = normalizeBalance(totalRemaining);
  return {
    events,
    targetEvent,
    targetEvents,
    paymentCredit,
    netBalance,
  };
};

const getCurrentCycleSnapshot = (member, payments, currentBusinessDateStr = formatDate(new Date()), options = {}) => {
  const { includeToday = false } = options;
  if (!member || !currentBusinessDateStr) {
    return {
      snapshotDate: '',
      currentDueDate: '',
      previousCycleDueDate: '',
      cycleStart: '',
      cycleEnd: '',
      accruedGross: 0,
      fullCycleGross: 0,
      overpaymentCarriedForward: 0,
      totalAdvance: 0,
      totalDeductions: 0,
      totalBonus: 0,
      totalSalaryPaidInCycle: 0,
      unpaidAsOfToday: 0,
      totalPayableOnDueDate: 0,
      accrualLabel: includeToday ? 'including today' : 'excluding today',
    };
  }

  const currentDueDate = getLedgerTargetDueDate(member, currentBusinessDateStr);
  if (!currentDueDate) {
    return {
      snapshotDate: currentBusinessDateStr,
      currentDueDate: '',
      previousCycleDueDate: '',
      cycleStart: '',
      cycleEnd: '',
      accruedGross: 0,
      fullCycleGross: 0,
      overpaymentCarriedForward: 0,
      totalAdvance: 0,
      totalDeductions: 0,
      totalBonus: 0,
      totalSalaryPaidInCycle: 0,
      unpaidAsOfToday: 0,
      totalPayableOnDueDate: 0,
      accrualLabel: includeToday ? 'including today' : 'excluding today',
    };
  }

  const cycleStart = getCycleStartDate(member, currentDueDate);
  const cycleEnd = currentDueDate; // payday is the last day of the cycle (inclusive)
  const previousCycleDueDate = getPreviousSalaryDueDateString(member, currentDueDate);
  const effectiveRangeEnd = includeToday ? currentBusinessDateStr : addDays(currentBusinessDateStr, -1);
  const accruedDays = sumCycleSegmentDays(member, currentDueDate, effectiveRangeEnd);
  const fullCycleDays = sumCycleSegmentDays(member, currentDueDate, currentDueDate);
  const dailySalary = roundMoney(toAmount(member?.monthly_salary) / 30);
  const accruedGross = roundMoney(accruedDays * dailySalary);
  const fullCycleGross = roundMoney(fullCycleDays * dailySalary);

  const cyclePayments = getStaffPayments(payments, member.id).filter((payment) => (
    payment.advance_date >= cycleStart && payment.advance_date <= currentBusinessDateStr
  ));
  const totalAdvance = roundMoney(cyclePayments
    .filter(payment => payment.payment_type === 'advance')
    .reduce((sum, payment) => sum + getAdvanceImpact(payment), 0));
  const totalDeductions = roundMoney(cyclePayments
    .filter(payment => payment.payment_type === 'deduction')
    .reduce((sum, payment) => sum + toAmount(payment.amount), 0));
  const totalBonus = roundMoney(cyclePayments
    .filter(payment => payment.payment_type === 'bonus')
    .reduce((sum, payment) => sum + toAmount(payment.amount), 0));

  const ledger = buildSalaryLedger(member, payments, currentDueDate, currentBusinessDateStr);
  const targetEvents = ledger.targetEvents || [];
  const pastSalaryCarryApplied = roundMoney(targetEvents.reduce((sum, event) => sum + roundMoney(event.pastCycleCarryApplied || 0), 0));
  const priorAdjustmentCarryApplied = roundMoney(targetEvents.reduce((sum, event) => sum + roundMoney(event.carryForwardApplied || 0), 0));
  const overpaymentCarriedForward = roundMoney(pastSalaryCarryApplied + priorAdjustmentCarryApplied);
  const totalSalaryPaidApplied = roundMoney(targetEvents.reduce((sum, event) => sum + roundMoney(event.cycleSalaryPaid || 0), 0));
  const totalSalaryPaidInCycle = roundMoney(Math.max(0, totalSalaryPaidApplied - pastSalaryCarryApplied));

  const unpaidAsOfToday = roundMoney(Math.max(
    0,
    accruedGross - overpaymentCarriedForward - totalAdvance - totalDeductions - totalSalaryPaidInCycle
  ));
  const totalPayableOnDueDate = roundMoney(Math.max(
    0,
    fullCycleGross - overpaymentCarriedForward - totalAdvance - totalDeductions - totalSalaryPaidInCycle
  ));

  return {
    snapshotDate: currentBusinessDateStr,
    currentDueDate,
    previousCycleDueDate,
    cycleStart,
    cycleEnd,
    accruedGross,
    fullCycleGross,
    overpaymentCarriedForward,
    totalAdvance,
    totalDeductions,
    totalBonus,
    totalSalaryPaidInCycle,
    unpaidAsOfToday,
    totalPayableOnDueDate,
    accrualLabel: includeToday ? 'including today' : 'excluding today',
  };
};

export const calculateProjectedSalaryDue = (member, payments, dueDateStr, asOfDateStr = dueDateStr) => {
  if (!member || !dueDateStr) {
    return { grossSalary: 0, debtBeforeSalary: 0, payable: 0, carryForward: 0, salaryPaidInCycle: 0, netBalance: 0 };
  }

  const eventEnd = dueDateStr < asOfDateStr ? dueDateStr : asOfDateStr;
  const ledger = buildSalaryLedger(member, payments, dueDateStr, asOfDateStr || dueDateStr);
  const targetEvents = ledger.targetEvents || [];
  return {
    grossSalary: roundMoney(targetEvents.reduce((sum, event) => sum + roundMoney(event.grossSalary || 0), 0)),
    debtBeforeSalary: calculateCurrentAdvanceBalance(member, payments, eventEnd),
    payable: roundMoney(Math.max(0, ledger.netBalance)),
    carryForward: roundMoney(Math.max(0, -ledger.netBalance)),
    salaryPaidInCycle: roundMoney(targetEvents.reduce((sum, event) => sum + roundMoney(event.cycleSalaryPaid || 0), 0)),
    netBalance: roundMoney(ledger.netBalance),
  };
};

export const calculateCycleOnlySalaryDue = (member, payments, dueDateStr, asOfDateStr = dueDateStr) => {
  if (!member || !dueDateStr) {
    return { grossSalary: 0, debtBeforeSalary: 0, payable: 0, carryForward: 0, salaryPaidInCycle: 0, netBalance: 0 };
  }

  const ledger = buildSalaryLedger(member, payments, dueDateStr, asOfDateStr || dueDateStr);
  const targetEvents = ledger.targetEvents || [];
  const targetRemaining = roundMoney(targetEvents.reduce((sum, event) => sum + roundMoney(event.remaining || 0), 0));
  return {
    grossSalary: roundMoney(targetEvents.reduce((sum, event) => sum + roundMoney(event.grossSalary || 0), 0)),
    debtBeforeSalary: 0,
    payable: roundMoney(Math.max(0, targetRemaining)),
    carryForward: roundMoney(Math.max(0, -targetRemaining)),
    salaryPaidInCycle: roundMoney(targetEvents.reduce((sum, event) => sum + roundMoney(event.cycleSalaryPaid || 0), 0)),
    netBalance: targetRemaining,
  };
};

export const calculateCurrentAdvanceBalance = (member, payments, asOfDateStr = formatDate(new Date())) => {
  if (!member || !asOfDateStr) return 0;

  const targetDue = getLedgerTargetDueDate(member, asOfDateStr);
  if (!targetDue) return 0;

  const ledger = buildSalaryLedger(member, payments, targetDue, asOfDateStr);
  return roundMoney(ledger.events.reduce((sum, event) => {
    if (event.advanceOnlyTotal <= 0) return sum;
    if (event.dueDate > asOfDateStr || event.remaining > 0) {
      return sum + event.advanceOnlyTotal;
    }
    return sum;
  }, 0));
};

export const getEffectiveSalaryDueSummary = (member, payments, asOfDateStr = formatDate(new Date()), options = {}) => {
  const { rollOverdue = true } = options;
  if (!member || !asOfDateStr) {
    return { dueDate: '', payable: 0, grossSalary: 0, debtBeforeSalary: 0, carryForward: 0, currentDueSettled: false };
  }
  const asOf = parseDate(asOfDateStr);
  if (!asOf) {
    return { dueDate: '', payable: 0, grossSalary: 0, debtBeforeSalary: 0, carryForward: 0, currentDueSettled: false };
  }

  const currentDue = getSalaryDueDateString(member, asOf.getFullYear(), asOf.getMonth());
  const previousDue = getPreviousSalaryDueDateString(member, currentDue);
  const previousSummary = previousDue ? calculateProjectedSalaryDue(member, payments, previousDue, asOfDateStr) : null;
  if (previousSummary && previousDue < asOfDateStr && roundMoney(previousSummary.payable) > 1 && currentDue > asOfDateStr) {
    return {
      ...previousSummary,
      dueDate: rollOverdue ? asOfDateStr : previousDue,
      currentDueSettled: false,
    };
  }

  const currentSummary = calculateProjectedSalaryDue(member, payments, currentDue, asOfDateStr);
  if (currentDue <= asOfDateStr && roundMoney(currentSummary.payable) <= 1) {
    const nextDue = getNextSalaryDueDateString(member, currentDue);
    const nextSummary = calculateProjectedSalaryDue(member, payments, nextDue, asOfDateStr);
    const terminationDate = getTerminationDate(member);
    if (!(terminationDate && nextSummary.grossSalary <= 0 && nextSummary.payable <= 0)) {
      return {
        ...nextSummary,
        dueDate: nextDue,
        currentDueSettled: true,
      };
    }
  }

  return {
    ...currentSummary,
    dueDate: rollOverdue && currentDue <= asOfDateStr && currentSummary.payable > 0 ? asOfDateStr : currentDue,
    currentDueSettled: false,
  };
};

export const blankSalarySummary = () => ({
  dueDate: '',
  payable: 0,
  grossSalary: 0,
  debtBeforeSalary: 0,
  carryForward: 0,
  salaryPaidInCycle: 0,
  netBalance: 0,
});

export const getOutstandingSalaryBalanceOnDate = (member, payments, asOfDateStr = formatDate(new Date())) => {
  if (!member || !asOfDateStr) return 0;
  const asOf = parseDate(asOfDateStr);
  if (!asOf) return 0;

  const currentDue = getSalaryDueDateString(member, asOf.getFullYear(), asOf.getMonth());
  const previousDue = getPreviousSalaryDueDateString(member, currentDue);
  const previousSummary = previousDue ? calculateProjectedSalaryDue(member, payments, previousDue, asOfDateStr) : null;
  const previousPayable = roundMoney(previousSummary?.payable || 0);

  if (previousSummary && previousDue < asOfDateStr && previousPayable > 1 && currentDue > asOfDateStr) {
    return previousPayable;
  }

  if (currentDue <= asOfDateStr) {
    return roundMoney(calculateProjectedSalaryDue(member, payments, currentDue, asOfDateStr).payable || 0);
  }

  return 0;
};

export const getSalarySummaryForDisplayDate = (member, payments, targetDateStr, currentBusinessDateStr = formatDate(new Date())) => {
  if (!member || !targetDateStr) return blankSalarySummary();
  const parsedTarget = parseDate(targetDateStr);
  if (!parsedTarget) return blankSalarySummary();

  const dueDate = getSalaryDueDateString(member, parsedTarget.getFullYear(), parsedTarget.getMonth());
  if (targetDateStr === currentBusinessDateStr) {
    return getEffectiveSalaryDueSummary(member, payments, targetDateStr, { rollOverdue: true });
  }
  if (dueDate !== targetDateStr) {
    return blankSalarySummary();
  }
  if (targetDateStr < currentBusinessDateStr) {
    return { ...calculateProjectedSalaryDue(member, payments, dueDate, dueDate), dueDate };
  }
  return { ...calculateCycleOnlySalaryDue(member, payments, dueDate, currentBusinessDateStr), dueDate };
};

export const getSalaryDueRowsForRange = (staffList, payments, fromDateStr, toDateStr, currentBusinessDateStr = formatDate(new Date())) => {
  if (!fromDateStr || !toDateStr) return [];

  const fromDate = parseDate(fromDateStr);
  const toDate = parseDate(toDateStr);
  if (!fromDate || !toDate || fromDate > toDate) return [];

  const rows = [];
  (staffList || []).forEach((member) => {
    if (!member) return;

    if (currentBusinessDateStr >= fromDateStr && currentBusinessDateStr <= toDateStr) {
      const currentSummary = getEffectiveSalaryDueSummary(member, payments, currentBusinessDateStr, { rollOverdue: true });
      if (currentSummary.payable > 0 && currentSummary.dueDate === currentBusinessDateStr) {
        rows.push({
          member,
          dueDate: currentBusinessDateStr,
          summary: currentSummary,
          isRolledToday: true,
        });
      }
    }

    let dueDate = getSalaryDueDateString(member, fromDate.getFullYear(), fromDate.getMonth());
    if (dueDate < fromDateStr) {
      dueDate = getNextSalaryDueDateString(member, dueDate);
    }

    let guard = 0;
    while (dueDate && dueDate <= toDateStr && guard < 36) {
      if (dueDate !== currentBusinessDateStr) {
        const summary = dueDate < currentBusinessDateStr
          ? { ...calculateProjectedSalaryDue(member, payments, dueDate, dueDate), dueDate }
          : { ...calculateCycleOnlySalaryDue(member, payments, dueDate, currentBusinessDateStr), dueDate };
        if (summary.payable > 0) {
          rows.push({
            member,
            dueDate,
            summary,
            isRolledToday: false,
          });
        }
      }
      dueDate = getNextSalaryDueDateString(member, dueDate);
      guard += 1;
    }
  });

  return rows.sort((a, b) => a.dueDate.localeCompare(b.dueDate) || String(a.member?.full_name || '').localeCompare(String(b.member?.full_name || '')));
};

export const getSalaryEventRowsForRange = (staffList, payments, fromDateStr, toDateStr, currentBusinessDateStr = formatDate(new Date())) => {
  if (!fromDateStr || !toDateStr) return [];

  const memberById = {};
  (staffList || []).forEach((member) => {
    memberById[String(member.id)] = member;
  });

  const paymentRows = (payments || [])
    .filter(isActivePayment)
    .filter((payment) => payment.advance_date && payment.advance_date >= fromDateStr && payment.advance_date <= toDateStr)
    .map((payment) => {
      const member = memberById[getStaffPaymentId(payment)];
      return {
        id: `payment-${payment.id}`,
        rowType: payment.payment_type,
        date: payment.advance_date,
        staffId: payment.staff ?? payment.staff_id,
        staffName: payment.staff_name || (member ? `${member.full_name || `${member.first_name || ''} ${member.last_name || ''}`.trim()}` : 'Unknown Staff'),
        department: member?.department_name || member?.designation || '',
        amount: roundMoney(payment.amount),
        status: payment.status || '',
        note: payment.reason || '',
        remainingDue: member ? getOutstandingSalaryBalanceOnDate(member, payments, payment.advance_date) : 0,
      };
    });

  const dueRows = getSalaryDueRowsForRange(staffList, payments, fromDateStr, toDateStr, currentBusinessDateStr)
    .map(({ member, dueDate, summary }) => ({
      id: `due-${member.id}-${dueDate}`,
      rowType: 'due',
      date: dueDate,
      staffId: member.id,
      staffName: member.full_name || `${member.first_name || ''} ${member.last_name || ''}`.trim(),
      department: member.department_name || member.designation || '',
      amount: roundMoney(summary.payable),
      status: 'due',
      note: 'Salary due',
      remainingDue: getOutstandingSalaryBalanceOnDate(member, payments, dueDate),
    }));

  const rowTypeOrder = {
    due: 0,
    salary: 1,
    bonus: 2,
    advance: 3,
    deduction: 4,
  };

  return [...paymentRows, ...dueRows].sort((a, b) => (
    b.date.localeCompare(a.date)
    || (rowTypeOrder[a.rowType] ?? 99) - (rowTypeOrder[b.rowType] ?? 99)
    || String(a.staffName || '').localeCompare(String(b.staffName || ''))
    || String(a.id || '').localeCompare(String(b.id || ''))
  ));
};

export const getSalaryAggregateRowsForRange = (staffList, payments, fromDateStr, toDateStr, currentBusinessDateStr = formatDate(new Date())) => {
  if (!fromDateStr || !toDateStr) return [];

  const eventRows = getSalaryEventRowsForRange(staffList, payments, fromDateStr, toDateStr, currentBusinessDateStr);
  const dueRows = getSalaryDueRowsForRange(staffList, payments, fromDateStr, toDateStr, currentBusinessDateStr);
  const dueStaffIds = new Set(dueRows.map(({ member }) => String(member?.id || '')));
  const rowsByStaff = {};

  const ensureRow = (member) => {
    if (!member?.id) return null;
    const staffId = String(member.id);
    if (!rowsByStaff[staffId]) {
      rowsByStaff[staffId] = {
        staffId,
        staffName: member.full_name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unknown Staff',
        department: member.department_name || member.designation || '',
        member,
        totalAdvance: 0,
        totalDeductions: 0,
        totalBonus: 0,
        totalSalaryPaid: 0,
        totalPaid: 0,
        snapshotDate: '',
        currentDueDate: '',
        previousCycleDueDate: '',
        cycleStart: '',
        cycleEnd: '',
        overpaymentCarriedForward: 0,
        unpaidAsOfToday: 0,
        totalPayableOnDueDate: 0,
        accrualLabel: 'excluding today',
        nextDueDate: '',
        nextDuePayable: 0,
        eventCount: 0,
      };
    }
    return rowsByStaff[staffId];
  };

  (staffList || []).forEach((member) => {
    if (!member?.id) return;
    const staffId = String(member.id);
    const activeOverlap = doesStaffOverlapDateRange(member, fromDateStr, toDateStr);
    const payrollActivity = doesStaffHavePayrollActivityInRange(member, payments, fromDateStr, toDateStr);
    if (!activeOverlap && !payrollActivity && !dueStaffIds.has(staffId)) return;
    ensureRow(member);
  });

  eventRows.forEach((row) => {
    const aggregate = rowsByStaff[String(row.staffId || '')];
    if (!aggregate) return;
    const amount = roundMoney(row.amount || 0);
    aggregate.eventCount += 1;
    if (row.rowType === 'advance') {
      aggregate.totalAdvance = roundMoney(aggregate.totalAdvance + amount);
      aggregate.totalPaid = roundMoney(aggregate.totalPaid + amount);
    } else if (row.rowType === 'salary') {
      aggregate.totalSalaryPaid = roundMoney(aggregate.totalSalaryPaid + amount);
      aggregate.totalPaid = roundMoney(aggregate.totalPaid + amount);
    } else if (row.rowType === 'bonus') {
      aggregate.totalBonus = roundMoney(aggregate.totalBonus + amount);
    } else if (row.rowType === 'deduction') {
      aggregate.totalDeductions = roundMoney(aggregate.totalDeductions + amount);
    }
  });

  Object.values(rowsByStaff).forEach((aggregate) => {
    const snapshot = getCurrentCycleSnapshot(aggregate.member, payments, toDateStr, { includeToday: false });
    aggregate.snapshotDate = snapshot.snapshotDate;
    aggregate.currentDueDate = snapshot.currentDueDate;
    aggregate.previousCycleDueDate = snapshot.previousCycleDueDate;
    aggregate.cycleStart = snapshot.cycleStart;
    aggregate.cycleEnd = snapshot.cycleEnd;
    aggregate.overpaymentCarriedForward = snapshot.overpaymentCarriedForward;
    aggregate.totalAdvance = snapshot.totalAdvance;
    aggregate.totalDeductions = snapshot.totalDeductions;
    aggregate.totalBonus = snapshot.totalBonus;
    aggregate.unpaidAsOfToday = snapshot.unpaidAsOfToday;
    aggregate.totalPayableOnDueDate = snapshot.totalPayableOnDueDate;
    aggregate.accrualLabel = snapshot.accrualLabel;
    aggregate.nextDueDate = snapshot.currentDueDate;
    aggregate.nextDuePayable = snapshot.totalPayableOnDueDate;
  });

  return Object.values(rowsByStaff).sort((a, b) => (
    b.unpaidAsOfToday - a.unpaidAsOfToday
    || b.totalPayableOnDueDate - a.totalPayableOnDueDate
    || a.staffName.localeCompare(b.staffName)
  ));
};
